import React from 'react';
import {
  Box,
  ListItem,
  Typography,
  Paper,
  Chip,
  Card,
  CardContent,
  Link,
} from '@mui/material';

const formatCurrency = (num) => {
  if (num === null || num === undefined) return '-';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 2 }).format(num);
};

const formatNumber = (num, decimals = 2) => {
  if (num === null || num === undefined) return '-';
  return Number(num).toLocaleString(undefined, { maximumFractionDigits: decimals });
};

const formatPercent = (num) => {
  if (num === null || num === undefined) return '-';
  return `${Number(num).toFixed(2)}%`;
};

const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  try {
    return new Date(dateString).toLocaleString();
  } catch {
    return dateString;
  }
};

const MetricRow = ({ label, value }) => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
    <Typography variant="body2" color="text.secondary">{label}</Typography>
    <Typography variant="body2" sx={{ fontWeight: 500 }}>{value}</Typography>
  </Box>
);

const normalizeKeywordList = (value) => {
  if (!value) return [];
  if (Array.isArray(value)) return value;
  if (typeof value === 'string') {
    // Some pipelines can materialize keyword arrays as comma-separated strings.
    if (value.includes(',')) return value.split(',').map((s) => s.trim()).filter(Boolean);
    return [value];
  }
  return [];
};

const WalletUsersCard = ({ hit }) => {
  const wallet = hit._source || {};
  const address = wallet.wallet_address || hit._id;
  const explorerUrl = address ? `https://debank.com/profile/${address}` : null;

  return (
    <ListItem
      sx={{
        borderBottom: '1px solid #e0e0e0',
        flexDirection: 'column',
        alignItems: 'stretch',
        py: 2,
        px: 0,
      }}
    >
      <Card elevation={1} sx={{ width: '100%', backgroundColor: '#fafafa', overflow: 'visible' }}>
        <CardContent sx={{ pb: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2, wordBreak: 'break-all' }}>
                {address || 'Unknown Wallet'}
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap' }}>
                {normalizeKeywordList(wallet.chains).slice(0, 6).map((chain) => (
                  <Chip key={chain} size="small" label={chain} variant="outlined" />
                ))}
                {normalizeKeywordList(wallet.labels).slice(0, 4).map((label) => (
                  <Chip key={label} size="small" label={label} color="secondary" variant="outlined" />
                ))}
                {normalizeKeywordList(wallet.tags).slice(0, 4).map((tag) => (
                  <Chip key={tag} size="small" label={tag} color="info" />
                ))}
              </Box>
            </Box>
            <Chip label={hit._score != null ? hit._score.toFixed(2) : '-'} size="small" variant="outlined" color="primary" />
          </Box>

          <Paper elevation={0} sx={{ p: 1.5, mb: 2, backgroundColor: 'grey.50', border: '1px solid', borderColor: 'grey.200' }}>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>Portfolio</Typography>
            <MetricRow label="Total" value={formatCurrency(wallet.portfolio_total_usd)} />
            <MetricRow label="Cash" value={formatCurrency(wallet.portfolio_cash_usd)} />
            <MetricRow label="Fungibles" value={formatCurrency(wallet.portfolio_fungibles_usd)} />
            <MetricRow label="NFTs" value={formatCurrency(wallet.portfolio_nfts_usd)} />
            <MetricRow label="Cash %" value={formatPercent(wallet.allocation_cash_pct)} />
            <MetricRow label="Fungibles %" value={formatPercent(wallet.allocation_fungibles_pct)} />
            <MetricRow label="NFTs %" value={formatPercent(wallet.allocation_nfts_pct)} />
          </Paper>

          <Paper elevation={0} sx={{ p: 1.5, mb: 2, backgroundColor: 'grey.50', border: '1px solid', borderColor: 'grey.200' }}>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>PnL / Activity</Typography>
            <MetricRow label="PnL % Total" value={formatPercent(wallet.pnl_pct_total)} />
            <MetricRow label="PnL Net Invested" value={formatCurrency(wallet.pnl_net_invested_usd)} />
            <MetricRow label="PnL Realized" value={formatCurrency(wallet.pnl_realized_usd)} />
            <MetricRow label="PnL Unrealized" value={formatCurrency(wallet.pnl_unrealized_usd)} />
            <MetricRow label="Volume 24h" value={formatCurrency(wallet.volume_usd_24h)} />
            <MetricRow label="Volume 7d" value={formatCurrency(wallet.volume_usd_7d)} />
            <MetricRow label="Volume 30d" value={formatCurrency(wallet.volume_usd_30d)} />
            <MetricRow label="Trades 24h" value={formatNumber(wallet.num_trades_24h, 0)} />
            <MetricRow label="Trades 30d" value={formatNumber(wallet.num_trades_30d, 0)} />
          </Paper>

          {normalizeKeywordList(wallet.fungible_positions_tokens_list).length > 0 && (
            <Paper elevation={0} sx={{ p: 1.5, mb: 2, backgroundColor: 'grey.50', border: '1px solid', borderColor: 'grey.200' }}>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>Fungible Positions (sample)</Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {normalizeKeywordList(wallet.fungible_positions_tokens_list).slice(0, 12).map((tokenAddrOrSym) => (
                  <Chip key={tokenAddrOrSym} size="small" label={tokenAddrOrSym} variant="outlined" />
                ))}
              </Box>
            </Paper>
          )}

          <Paper elevation={0} sx={{ p: 1.5, backgroundColor: 'grey.50', border: '1px solid', borderColor: 'grey.200' }}>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>Timestamps</Typography>
            <MetricRow label="Last Trade" value={formatDate(wallet.last_trade_timestamp)} />
            <MetricRow label="Last Enriched" value={formatDate(wallet.zerion_last_enriched_at)} />
          </Paper>

          <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'grey.200' }}>
            <Typography variant="caption" sx={{ color: 'text.secondary', wordBreak: 'break-all', fontSize: '0.7rem' }}>
              {explorerUrl ? (
                <Link href={explorerUrl} target="_blank" rel="noopener noreferrer" sx={{ color: 'inherit', textDecoration: 'underline' }}>
                  {address}
                </Link>
              ) : (
                address
              )}
            </Typography>
          </Box>
        </CardContent>
      </Card>
    </ListItem>
  );
};

export default WalletUsersCard;
